import type WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Badge } from '@gik/ui/Badge';
import { Grid } from '@gik/ui/Grid';
import { HTMLParser } from '@gik/ui/HTMLParser';
import { Image } from '@gik/ui/Image';
import React from 'react';

export type SituationsNavProps = {
  items: WordpressSituation[];
  value?: number;
  onChange?(value: number): void;
} & UIComponent;

export type TaxonomyNavItem = {
  id: number;
  slug: string;
  parent: number;
  icon?: string;
  title: string;
  variant: string;
};

function SituationsNavComp({ items, value = -1, onChange = noop }: SituationsNavProps): React.ReactElement {
  const bem = useBemCN('situations-nav');

  const _items = React.useMemo(() => {
    const newItems: WordpressSituation[] = [
      {
        id: -1,
        slug: 'all',
        parent: 0,
        name: 'Show All',
        acf: {
          variant: 'all',
        },
      },
    ];
    return newItems.concat(items);
  }, [items]);

  return (
    <div {...bem()}>
      <Grid>
        {_items?.map(item => {
          const icon = item.acf.taxonomy_svg_icon || item.acf.tax_thumb_image;

          return (
            <Badge
              pill
              key={item.id}
              {...bem('item', [
                {
                  selected: item.id === value,
                },
                {
                  [item.acf.variant]: item.acf.variant,
                },
              ])}
              onClick={() => onChange(item.id)}
            >
              {icon && <Image src={icon} />}
              <HTMLParser rawHtml={item.name} />
            </Badge>
          );
        })}
      </Grid>
    </div>
  );
}

export const SituationsNav = withComponentErrorBoundary(SituationsNavComp);
